/** Раздел «Выписки»: ответы ручек `/api/entrepreneurs`, `/api/banks`, `/api/bank-statements`. */
import { apiFetch, query } from "@/lib/api";

export type Entrepreneur = { id: number; name: string; inn: string | null; archived_at: string | null };
export type Bank = { id: number; name: string; bik: string | null; archived_at: string | null };

export type Statement = {
  id: number;
  /** `null` — не распознан: ИП или банк назначают руками. */
  entrepreneur_id: number | null;
  entrepreneur_name: string | null;
  bank_id: number | null;
  bank_name: string | null;
  /** Что нашлось в файле — подсказка, чего не хватает в справочниках. */
  account: string | null;
  /** Вид и банк счёта — из наших счетов, если счёт уже встречался в разобранной выписке. */
  account_kind_name: string | null;
  account_bank_name: string | null;
  /** Что за файл: `statement`, `acquiring_operations`, `acquiring_credits`, `unknown`; `null` — не разбирали. */
  kind_code: string | null;
  kind_name: string | null;
  /** Как распознан: по каким признакам узнан тип, ИП и счёт, чем сверен. */
  recognition: string | null;
  found_inn: string | null;
  found_bik: string | null;
  status_code: string;
  status_name: string;
  file_name: string;
  file_size: number;
  user_id: number;
  user_name: string | null;
  /** ISO с `Z`. */
  uploaded_at: string;
  /** Разбор в операции: период выписки, сколько операций, почему не разобрана. */
  date_from: string | null;
  date_to: string | null;
  operation_count: number | null;
  parse_error: string | null;
};

/** Справочник — ИП или банк: у обоих имя, код и архив, разное только имя кода. */
export type DirectoryKind = {
  path: "/api/entrepreneurs" | "/api/banks";
  codeField: "inn" | "bik";
  codeLabel: string;
  codeHint: string;
  title: string;
  placeholder: string;
};

export const ENTREPRENEURS: DirectoryKind = {
  path: "/api/entrepreneurs",
  codeField: "inn",
  codeLabel: "ИНН",
  codeHint: "12 цифр у ИП, 10 у юрлица; необязателен",
  title: "ИП",
  placeholder: "ИП Иванов И. И.",
};

export const BANKS: DirectoryKind = {
  path: "/api/banks",
  codeField: "bik",
  codeLabel: "БИК",
  codeHint: "9 цифр; необязателен",
  title: "Банки",
  placeholder: "Сбербанк",
};

export type DirectoryRow = { id: number; name: string; code: string | null; archived_at: string | null };

export async function loadDirectory(kind: DirectoryKind, archived = false): Promise<DirectoryRow[]> {
  const rows = await apiFetch<Record<string, unknown>[]>(`${kind.path}${query({ archived })}`);
  return rows.map((r) => ({
    id: r["id"] as number,
    name: r["name"] as string,
    code: (r[kind.codeField] as string | null) ?? null,
    archived_at: (r["archived_at"] as string | null) ?? null,
  }));
}

export function createDirectoryRow(kind: DirectoryKind, name: string, code: string): Promise<unknown> {
  return apiFetch(kind.path, { method: "POST", body: JSON.stringify({ name, [kind.codeField]: code }) });
}

export function patchDirectoryRow(
  kind: DirectoryKind,
  id: number,
  patch: { name?: string; code?: string; archived?: boolean },
): Promise<unknown> {
  const body: Record<string, unknown> = {};
  if (patch.name !== undefined) body["name"] = patch.name;
  if (patch.code !== undefined) body[kind.codeField] = patch.code;
  if (patch.archived !== undefined) body["archived"] = patch.archived;
  return apiFetch(`${kind.path}/${id}`, { method: "PATCH", body: JSON.stringify(body) });
}

export function loadStatements(): Promise<Statement[]> {
  return apiFetch<Statement[]>("/api/bank-statements");
}

/** Один файл без ИП и банка: сервер узнаёт их по ИНН и БИК в файле. */
export function uploadStatement(file: File): Promise<Statement> {
  const form = new FormData();
  form.append("file", file);
  return apiFetch<Statement>("/api/bank-statements", { method: "POST", body: form });
}

export function assignStatement(
  id: number,
  patch: { entrepreneur_id?: number; bank_id?: number },
): Promise<Statement> {
  return apiFetch<Statement>(`/api/bank-statements/${id}`, { method: "PATCH", body: JSON.stringify(patch) });
}

/** Распознать заново всё, где нет ИП или банка: после пополнения справочников. */
export function recognizeStatements(): Promise<{ checked: number; recognized: number }> {
  return apiFetch("/api/bank-statements/recognize", { method: "POST" });
}

export function removeStatement(id: number): Promise<unknown> {
  return apiFetch(`/api/bank-statements/${id}`, { method: "DELETE" });
}

export function statementFileUrl(id: number): string {
  return `/api/bank-statements/${id}/file`;
}

/** Что принимает сервер (`app/core/modules/statement/files.py`). */
export const ACCEPT = ".xlsx,.xls,.csv,.txt,.pdf";
